import type Plan from '@/classes/Plan'
import { inmanOapPaymentOptions } from '@/data/inmanOapPaymentOptions'
import { OapProvider } from '@/data/oapProviders'
import { sepioOapPaymentOptions } from '@/data/sepioOapPaymentOptions'
import { PlanOapPaymentType } from '@/enums/PlanOapPaymentType'
import { getBlankEft } from '@/mixins/eft'
import { useSessionStore } from '@/stores/session'
import type {
	InmanOapPayment,
	OapPayment,
	OapPaymentPlan,
	SepioEftPaymentDetails,
	SepioOapPayment,
} from '@/types'
import { captureException } from '@sentry/vue'

export function getDefaultSepioPaymentPlan(): SepioEftPaymentDetails {
	return {
		paymentType: PlanOapPaymentType.EFT,
		paymentDetails: getBlankEft(),
	}
}

export function getDefaultInmanPaymentPlan() {
	return null
}

/** Modifies the given plan with appropriate OAP defaults */
export function initializePlanOap(plan: Plan) {
	const sessionStore = useSessionStore()
	const provider = sessionStore.activeOapProvider
	const { getPlanByPaymentCount } = useOapPlanProvider(provider ?? null)
	const paymentPlan = getPlanByPaymentCount(1)

	if (!provider || !paymentPlan) {
		captureException(new Error('Cannot determine OAP for session'))
		return
	}

	if (plan.oapPayment.provider !== provider) {
		switch (provider) {
			case OapProvider.SEPIO_GUARD:
				plan.oapPayment = {
					provider,
					paymentPlanId: paymentPlan.id,
					paymentPlan: getDefaultSepioPaymentPlan(),
				}
				break

			case OapProvider.INMAN:
				plan.oapPayment = {
					provider,
					paymentPlanId: paymentPlan.id,
					paymentPlan: null, // Initialized when they get to the form
				}
		}
	}
}

export function useOapPlanProvider(oapProvider: OapProvider | null) {
	const defaultOapPrice = 499
	const useProd = import.meta.env.VITE_USE_PROD_SEPIO_PLANS === 'true'
	const sessionStore = useSessionStore()

	const getPricePointId = () => {
		// The stage employer ids are the same as the production employer ids because the database is copied over.
		// Because these ids don't match the ids for Sepio's QA environment, we are keeping this logic to support stage and local testing
		if (!useProd) {
			return getPaymentPlansOptions()[0]?.pricePointId
		}

		return sessionStore.activeFh?.oap?.data?.firstLineItem?.data?.product?.data
			?.productCode
	}

	const getPaymentPlansOptions = () => {
		if (oapProvider === OapProvider.SEPIO_GUARD) {
			return sepioOapPaymentOptions
		}

		if (oapProvider === OapProvider.INMAN) {
			return inmanOapPaymentOptions
		}

		return []
	}

	const getPlanById = (
		id: string | undefined | null
	): OapPaymentPlan | undefined => {
		return getPaymentPlansOptions().find((option) => option.id === id)
	}

	/**
	 * Finds an OAP payment option by paymentCount
	 */
	const getPlanByPaymentCount = (count: number): OapPaymentPlan | undefined => {
		const pricePointId = getPricePointId()

		return getPaymentPlansOptions().find(
			(opt) => opt.pricePointId === pricePointId && opt.paymentCount === count
		)
	}

	function updateOapPaymentCount(
		oapPayment: OapPayment,
		newCount: number
	): SepioOapPayment | InmanOapPayment | undefined {
		const newPaymentPlan = getPlanByPaymentCount(newCount)

		if (!newPaymentPlan || !oapPayment.provider) {
			return undefined
		}

		return {
			...oapPayment,
			paymentPlanId: newPaymentPlan?.id,
		}
	}

	return {
		getPricePointId,
		getPaymentPlansOptions,
		getPlanByPaymentCount,
		getPlanById,
		defaultOapPrice,
		updateOapPaymentCount,
	}
}
